import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { TrendingUp, Euro, Clock } from "lucide-react";

const DEFAULT_TAX_RATE = 19;

const getTaxRate = (companySettings) => {
  const t = companySettings?.tax_rate;
  return t != null ? parseFloat(t) : DEFAULT_TAX_RATE;
};

// Total tracked time in minutes for an order.
// Prefer backend-maintained total_work_minutes; fall back to the locally computed value.
const getTrackedMinutes = (order, totals) => {
  const fromOrder = parseInt(order?.total_work_minutes || 0);
  const fromTotals = parseInt(totals?.recordedMinutes || 0);
  return fromOrder > 0 ? fromOrder : fromTotals;
};

// Compute material EK/VK totals.
// VK is taken from `m.kosten` (the authoritative stored cost) for consistency with the
// summary card; falls back to `anzahl × einzelpreis` if `kosten` is missing/zero.
const computeMaterialCosts = (order) => {
  let ekTotal = 0;
  let vkTotal = 0;
  (order?.tasks || []).forEach((t) => {
    (t.materialien || []).forEach((m) => {
      const anzahl = parseFloat(m.anzahl) || 0;
      const vkPerUnit = parseFloat(m.einzelpreis) || 0;
      let ekPerUnit = parseFloat(m.einkaufspreis) || 0;
      if (!ekPerUnit && vkPerUnit && m.marge_prozent > 0) {
        ekPerUnit = vkPerUnit / (1 + m.marge_prozent / 100);
      }
      const storedCost = parseFloat(m.kosten);
      vkTotal += !isNaN(storedCost) && storedCost > 0 ? storedCost : anzahl * vkPerUnit;
      ekTotal += anzahl * ekPerUnit;
    });
  });
  return { ekTotal, vkTotal };
};

// Compute full cost breakdown incl. labor from tracked time * stundenlohn.
const computeCosts = (order, totals, companySettings) => {
  const { ekTotal, vkTotal: materialVk } = computeMaterialCosts(order);
  const stundenlohn = parseFloat(companySettings?.stundenlohn || 0);
  const taxRate = getTaxRate(companySettings);
  const trackedMinutes = getTrackedMinutes(order, totals);
  const trackedHours = trackedMinutes / 60;
  const laborNet = trackedHours * stundenlohn;

  const vkTotal = materialVk + laborNet;
  const gewinn = vkTotal - ekTotal; // labor has no EK -> full revenue = profit
  const margePct = vkTotal > 0 ? (gewinn / vkTotal) * 100 : 0;
  const steuer = (vkTotal * taxRate) / 100;
  const brutto = vkTotal + steuer;
  return {
    ekTotal,
    materialVk,
    laborNet,
    trackedMinutes,
    trackedHours,
    stundenlohn,
    vkTotal,
    gewinn,
    margePct,
    taxRate,
    steuer,
    brutto,
  };
};

const formatHours = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}min` : `${m}min`;
};

export const AdminCostCard = ({ order, user, totals, companySettings }) => {
  if (user?.role !== "admin") return null;
  const hasMaterials = (order?.tasks || []).some((t) => (t.materialien || []).length > 0);
  const trackedMinutes = getTrackedMinutes(order, totals);
  const stundenlohn = parseFloat(companySettings?.stundenlohn || 0);
  const hasLabor = trackedMinutes > 0 && stundenlohn > 0;
  if (!hasMaterials && !hasLabor) return null;

  const {
    ekTotal,
    materialVk,
    laborNet,
    trackedHours,
    vkTotal,
    gewinn,
    margePct,
    taxRate,
    steuer,
    brutto,
  } = computeCosts(order, totals, companySettings);

  return (
    <Card data-testid="detail-admin-cost-card" className="border-2 border-emerald-200 bg-emerald-50/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-emerald-600" />
          Kosten &amp; Gewinn (nur Admin)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div className="bg-white rounded-lg p-3 border">
            <p className="text-xs text-muted-foreground">EK-Summe (Material)</p>
            <p className="text-lg font-bold text-slate-700" data-testid="admin-ek-total">{ekTotal.toFixed(2)} €</p>
          </div>
          <div className="bg-white rounded-lg p-3 border">
            <p className="text-xs text-muted-foreground">VK-Summe (netto)</p>
            <p className="text-lg font-bold text-blue-700" data-testid="admin-vk-total">{vkTotal.toFixed(2)} €</p>
            <p className="text-[10px] text-muted-foreground">
              Material {materialVk.toFixed(2)} € + Arbeit {laborNet.toFixed(2)} €
            </p>
          </div>
          <div className="bg-white rounded-lg p-3 border border-emerald-200">
            <p className="text-xs text-muted-foreground">Gewinn</p>
            <p className="text-lg font-bold text-emerald-700" data-testid="admin-profit">
              {gewinn >= 0 ? "+" : ""}{gewinn.toFixed(2)} €
            </p>
            <p className="text-[10px] text-emerald-600/80 font-medium">({margePct.toFixed(1)}% Marge)</p>
          </div>
          <div className="bg-white rounded-lg p-3 border">
            <p className="text-xs text-muted-foreground">MwSt ({taxRate}%)</p>
            <p className="text-lg font-bold text-slate-700" data-testid="admin-mwst">{steuer.toFixed(2)} €</p>
          </div>
        </div>

        {hasLabor && (
          <div className="bg-white/70 rounded-lg p-3 border text-xs flex items-center justify-between" data-testid="admin-labor-breakdown">
            <span className="flex items-center gap-2 text-muted-foreground">
              <Clock className="w-3.5 h-3.5" />
              Arbeitskosten: {formatHours(trackedMinutes)} × {stundenlohn.toFixed(2)} €/h
            </span>
            <span className="font-semibold text-foreground">
              {laborNet.toFixed(2)} € netto
            </span>
          </div>
        )}

        <Separator />
        <div className="flex items-center justify-between bg-primary/5 rounded-lg p-3">
          <span className="text-sm font-medium flex items-center gap-2">
            <Euro className="w-4 h-4" />Gesamt (VK brutto, inkl. MwSt)
          </span>
          <span className="text-xl font-bold text-primary" data-testid="admin-brutto">
            {brutto.toFixed(2)} €
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export { computeCosts, computeMaterialCosts, getTrackedMinutes, getTaxRate, DEFAULT_TAX_RATE };
